import type { PfCorrectionResponse } from "../services/pfCorrection";
import type { PowerQualityStatus } from "../services/powerQualityContract";
import { formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer, DG, UPS, PV, starter, breaker and
// busbar summaries for a calculated, a warned and a review-required study: a missing source
// or an unregistered rule is a review the engineer owes, not a design failure. INDETERMINATE
// is this module's own word: nothing could be computed (a target at or below the present
// power factor, an empty or repeated spectrum), which is a failure of the inputs.
export const STATUS_LABELS: Record<PowerQualityStatus, string> = {
  VALID: "Calculated",
  WARNING: "Calculated with warnings",
  REVIEW_REQUIRED: "Engineering review required",
  INDETERMINATE: "Cannot be computed",
};

export const STATUS_TONE: Record<PowerQualityStatus, "pass" | "fail" | "warning"> = {
  VALID: "pass",
  WARNING: "warning",
  REVIEW_REQUIRED: "warning",
  INDETERMINATE: "fail",
};

export function statusHeading(status: PowerQualityStatus): string {
  return STATUS_LABELS[status];
}

type PfCorrectionResultSummaryProps = {
  result: PfCorrectionResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): the compensation the bus needs,
// the bank rating at the declared voltage (with the reactor when one is declared), what a
// declared bank delivers, the capacitor terminal voltage against its rating, and the
// resonance orders with the matched characteristic orders. No figure is calculated here:
// every value is the engine's. Display rule A12: four significant figures on screen, the
// exact engine decimal in the tooltip (title).
export function PfCorrectionResultSummary({ result }: PfCorrectionResultSummaryProps) {
  const tone = STATUS_TONE[result.status];

  const compensation = formatQuantity(result.required_compensation_kvar, "kvar");
  const rating = formatQuantity(result.bank_rating_required_kvar, "kvar");
  const ratingWithReactor = formatQuantity(result.bank_rating_required_with_reactor_kvar, "kvar");
  const delivered = formatQuantity(result.declared_bank_delivered_kvar, "kvar");
  const terminalVoltage = formatQuantity(
    result.reactor?.capacitor_terminal_voltage_v ?? result.system_nominal_voltage_v,
    "V",
  );
  const resonance = formatQuantity(result.resonance?.resonance_order ?? null, "");
  const detuned = formatQuantity(result.resonance?.detuned_resonance_order ?? null, "");
  const matched = result.resonance?.matched_orders ?? [];

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result.status)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile}{" "}
          profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Compensation</dt>
          {/* Qc = P (tan φ1 − tan φ2), from the present to the target power factor. */}
          <dd data-summary-field="compensation" data-summary-value="text" title={compensation.exact ?? undefined}>
            {compensation.display} ({result.present_power_factor} → {result.target_power_factor})
          </dd>
        </div>
        <div>
          <dt>Bank rating</dt>
          {/* The rating at the declared capacitor voltage; with a reactor the lower figure. */}
          <dd
            data-summary-field="bank-rating"
            data-summary-value="text"
            title={[rating.exact, ratingWithReactor.exact].filter(Boolean).join(" · ") || undefined}
          >
            {rating.display} at {result.bank.rated_voltage_v} V
            {result.bank_rating_required_with_reactor_kvar !== null
              ? ` (${ratingWithReactor.display} with the reactor)`
              : ""}
          </dd>
        </div>
        <div>
          <dt>Declared bank delivers</dt>
          {/* Q_net at the network terminals; empty when no bank was declared. */}
          <dd data-summary-field="delivered" title={delivered.exact ?? undefined}>
            {delivered.display}
            {result.bank_covers_requirement === null
              ? ""
              : result.bank_covers_requirement
                ? " · covers the requirement"
                : " · below the requirement"}
          </dd>
        </div>
        <div>
          <dt>Capacitor voltage</dt>
          {/* U_c across the capacitor with a reactor, else the system voltage, against U_r. */}
          <dd
            data-summary-field="capacitor-voltage"
            data-summary-value="text"
            data-within-rating={result.reactor?.capacitor_voltage_within_rating ?? ""}
            title={terminalVoltage.exact ?? undefined}
          >
            {terminalVoltage.display} against {result.bank.rated_voltage_v} V rated
          </dd>
        </div>
        <div>
          <dt>Resonance order</dt>
          {/* h_r undetuned, h_p with the reactor; empty when S_sc was not established. */}
          <dd
            data-summary-field="resonance"
            data-summary-value="text"
            data-matched-orders={matched.join(",")}
            title={[resonance.exact, detuned.exact].filter(Boolean).join(" · ") || undefined}
          >
            {result.resonance === null
              ? resonance.display
              : `${resonance.display}${
                  result.resonance.detuned_resonance_order !== null
                    ? ` (detuned ${detuned.display})`
                    : ""
                }${matched.length > 0 ? ` · near order${matched.length > 1 ? "s" : ""} ${matched.join(", ")}` : ""}`}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={result.warnings.length}>{result.warnings.length}</dd>
        </div>
      </dl>
    </section>
  );
}

export default PfCorrectionResultSummary;
